import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DetailPanelGrid,
  useBackofficePageMode,
  useCustomizableDetailTabs,
} from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import { fetchMessageBusOverview } from './api';
import { MessageBusBarTile, MessageBusPageHeader, MessageBusSummaryTile } from './components';
import type { BackofficeMessageBusOverview } from './types';

const TABS: Array<{ id: 'overview'; label: string }> = [{ id: 'overview', label: 'Overview' }];
const DEFAULT_TILE_IDS = ['status', 'current-status', 'topics'];

export function BackofficeMessageBusPage() {
  useBackofficePageTitle('Message Bus');
  const { pageMode, setPageMode } = useBackofficePageMode();
  const [data, setData] = useState<BackofficeMessageBusOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const tabs = useCustomizableDetailTabs<'overview'>({
    preferenceScope: 'messagebus:tabs',
    builtInTabs: TABS,
    defaultTabId: 'overview',
    basePath: '/developer/messagebus',
    pageMode,
  });
  const activeCustomTab = tabs.activeCustomTab;
  const layoutPreferenceScope = activeCustomTab
    ? `messagebus:overview:layout:${activeCustomTab.id}`
    : 'messagebus:overview:layout';
  const defaultVisibleTileIds = activeCustomTab ? [] : DEFAULT_TILE_IDS;
  const showTabs = pageMode === 'edit' || tabs.customTabs.length > 0;

  const load = useCallback((signal?: AbortSignal, forceRefresh = false) => {
    setLoading(true);
    setError(null);
    return fetchMessageBusOverview(signal, forceRefresh)
      .then(setData)
      .catch((caught: unknown) => {
        if (!(caught instanceof DOMException && caught.name === 'AbortError')) {
          setError(
            caught instanceof Error ? caught.message : 'Failed to load message bus overview'
          );
        }
      })
      .finally(() => {
        if (!signal?.aborted) {
          setLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const refresh = useCallback(() => {
    setRefreshing(true);
    void load(undefined, true).finally(() => setRefreshing(false));
  }, [load]);

  const items = useMemo(
    () => [
      {
        id: 'status',
        title: 'Message bus status',
        layout: { w: 4, h: 2, minW: 2, minH: 2, maxW: 6, maxH: 4, isResizable: true },
        render: () =>
          data ? (
            <MessageBusSummaryTile
              title="Message bus status"
              panelId="status"
              rows={[
                { label: 'Provider', value: data.provider },
                { label: 'Status', value: data.status },
                { label: 'Topics', value: String(data.topicCount) },
                { label: 'Subscribers', value: String(data.subscriberCount) },
                { label: 'Active messages', value: String(data.totalActiveMessageCount) },
                { label: 'Dead letters', value: String(data.totalDeadLetterMessageCount) },
                { label: 'Generated at', value: new Date(data.generatedAt).toLocaleString() },
              ]}
            />
          ) : null,
      },
      {
        id: 'current-status',
        title: 'Current status',
        layout: { w: 8, h: 3, minW: 4, minH: 2, maxW: 12, maxH: 5, isResizable: true },
        render: () =>
          data ? (
            <MessageBusBarTile
              title="Current status"
              panelId="current-status"
              rows={data.topSubscribers.map((row) => ({
                ...row,
                topicHref: `/developer/messagebus/topics/${encodeURIComponent(row.topicName)}`,
                subscriberHref: `/developer/messagebus/topics/${encodeURIComponent(row.topicName)}/subscribers/${encodeURIComponent(row.subscriberName)}`,
              }))}
              emptyLabel="No subscribers found."
            />
          ) : null,
      },
      {
        id: 'topics',
        title: 'Topics',
        layout: { w: 8, h: 3, minW: 4, minH: 2, maxW: 12, maxH: 5, isResizable: true },
        render: () =>
          data ? (
            <MessageBusBarTile
              title="Topics"
              panelId="topics"
              rows={data.topics.map((topic) => ({
                topicName: topic.name,
                subscriberName: `${topic.subscriberCount} subscribers`,
                currentMessageCount: topic.currentMessageCount,
                activeMessageCount: topic.activeMessageCount,
                deadLetterMessageCount: topic.deadLetterMessageCount,
                topicHref: `/developer/messagebus/topics/${encodeURIComponent(topic.name)}`,
              }))}
              emptyLabel="No topics found."
            />
          ) : null,
      },
    ],
    [data]
  );

  return (
    <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        {/* extracted to keep message-bus page actions aligned across overview, topic, and subscriber */}
        <MessageBusPageHeader
          showTabs={showTabs}
          tabs={tabs}
          pageLabel="message bus page"
          pageMode={pageMode}
          onPageModeChange={setPageMode}
          onRefresh={refresh}
          refreshing={refreshing}
        />
        {loading && !data ? (
          <p className="text-body text-neutral-500">Loading message bus…</p>
        ) : null}
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-body text-red-700">
            {error}
          </div>
        ) : null}
        {data ? (
          <DetailPanelGrid
            resetKey={tabs.activeTabId}
            preferenceScope={layoutPreferenceScope}
            layoutMode={pageMode}
            cols={{ xxl: 12, lg: 12, md: 6, sm: 4, xs: 1, xxs: 1 }}
            defaultVisibleItemIds={defaultVisibleTileIds}
            addTileLayout={{ w: 2, h: 1 }}
            tileCatalogLabel="Add tile"
            items={items}
          />
        ) : null}
      </div>
    </main>
  );
}
